import React, { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './Tooltip.css';
import {
    calculateSimpleTooltipPosition,
    calculateTooltipPosition,
} from '../../utils/tooltipUtils';
import { usePointerHover } from '../../hooks/usePointerHover';

const DBG_DEFAULT = false;
// How long a pointer must settle on something before `onShow` fires. Only the
// side effect waits — the panel itself still opens immediately, so this is not
// perceptible; it just stops a cursor crossing a grid from triggering a fetch
// per card it passes over.
const SHOW_INTENT_MS = 200;
const EVT_ACTIVE = 'db:tooltip:active';
let activeId = null;
let idSeq = 0;

const setActive = (id) => {
    activeId = id;
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent(EVT_ACTIVE, { detail: id }));
};

const clearActive = (id) => {
    if (activeId !== id) return;
    setActive(null);
};

const Tooltip = ({
    children,
    content,
    className = '',
    wrapperClassName = '',
    debug = DBG_DEFAULT,
    positionOptions,
    positionMode = 'center',
    onShow,
}) => {
    const hasPointerHover = usePointerHover();
    const [isVisible, setIsVisible] = useState(false);
    const [pos, setPos] = useState({ top: 0, left: 0, goUpwards: true, goLeftwards: false });
    const wrapRef = useRef(null);
    const tipRef = useRef(null);
    const idRef = useRef(null);
    const rafRef = useRef(null);
    const showTimerRef = useRef(null);

    if (idRef.current === null) {
        idSeq += 1;
        idRef.current = `tt-${idSeq}`;
    }

    const updatePosNow = () => {
        if (!wrapRef.current) return;
        const r = wrapRef.current.getBoundingClientRect();

        // Use the actual tooltip dimensions if it's rendered, otherwise use estimates
        const tooltipEl = tipRef.current;
        const actualHeight = tooltipEl ? tooltipEl.offsetHeight : undefined;
        const actualWidth = tooltipEl ? tooltipEl.offsetWidth : undefined;

        if (positionMode === 'side') {
            const options = { ...positionOptions };
            if (actualHeight) options.estimatedHeight = actualHeight;
            if (actualWidth) options.estimatedWidth = actualWidth;
            const p = calculateTooltipPosition(r, options);
            setPos({ top: p.y, left: p.x, goUpwards: p.goUpwards, goLeftwards: p.goLeftwards });
        } else {
            const options = { ...positionOptions };
            if (actualHeight) options.estimatedHeight = actualHeight;
            if (actualWidth) options.estimatedWidth = actualWidth;
            const p = calculateSimpleTooltipPosition(r, options);
            setPos({ top: p.top, left: p.left, goUpwards: p.goUpwards, goLeftwards: false });
        }
    };

    const scheduleUpdatePos = () => {
        if (rafRef.current != null) return;
        rafRef.current = window.requestAnimationFrame(() => {
            rafRef.current = null;
            updatePosNow();
        });
    };

    const handleMouseEnter = () => {
        if (!hasPointerHover) return;
        setActive(idRef.current);
        scheduleUpdatePos();
        setIsVisible(true);
        // Deferred rather than fired immediately. `onShow` is how a consumer
        // loads whatever the panel describes, and a cursor crossing a grid on
        // its way somewhere else hovers every card it passes — each of which
        // would start a fetch nobody asked for. A short settle means only a
        // card the pointer actually rests on pays.
        if (onShow) {
            clearTimeout(showTimerRef.current);
            showTimerRef.current = setTimeout(onShow, SHOW_INTENT_MS);
        }
    };

    const handleMouseLeave = () => {
        if (!hasPointerHover) return;
        clearTimeout(showTimerRef.current);
        clearActive(idRef.current);
        setIsVisible(false);
    };

    const show = !!content && (debug || isVisible);

    useEffect(() => {
        if (!show) return undefined;
        scheduleUpdatePos();
        const onScroll = () => scheduleUpdatePos();
        const onResize = () => scheduleUpdatePos();
        window.addEventListener('scroll', onScroll, true);
        window.addEventListener('resize', onResize);

        // The panel's own size decides which way it flips and how far it gets
        // clamped, so a panel that changes size after it opens has to be placed
        // again. A build tooltip opens as a small skeleton and grows when the
        // fetch lands; near a viewport edge it keeps the placement it earned
        // while small, and the grown panel hangs off-screen until the pointer
        // leaves and comes back.
        let ro = null;
        if (tipRef.current && typeof ResizeObserver !== 'undefined') {
            ro = new ResizeObserver(() => scheduleUpdatePos());
            ro.observe(tipRef.current);
        }

        return () => {
            window.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', onResize);
            if (ro) ro.disconnect();
            if (rafRef.current != null) {
                window.cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const onActive = (evt) => {
            const nextId = evt?.detail ?? null;
            if (nextId !== idRef.current) {
                setIsVisible(false);
            }
        };
        window.addEventListener(EVT_ACTIVE, onActive);
        return () => {
            window.removeEventListener(EVT_ACTIVE, onActive);
            clearTimeout(showTimerRef.current);
            clearActive(idRef.current);
            if (rafRef.current != null) {
                window.cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
        };
    }, []);

    return (
        <div
            ref={wrapRef}
            className={`tooltip-wrapper${wrapperClassName ? ` ${wrapperClassName}` : ''}`}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            {children}
            {show && typeof window !== 'undefined' &&
                createPortal(
                    <div
                        id={idRef.current}
                        ref={tipRef}
                        className={`tooltip ${className}`}
                        style={{
                            position: 'fixed',
                            top: pos.top,
                            left: pos.left,
                            transform: positionMode === 'side'
                                ? `${pos.goUpwards ? 'translateY(-100%)' : 'translateY(0)'} ${pos.goLeftwards ? 'translateX(-100%)' : 'translateX(0)'}`
                                : (pos.goUpwards ? 'translate(-50%, -100%)' : 'translate(-50%, 0)'),
                            zIndex: 11000,
                        }}
                    >
                        {content}
                    </div>,
                    document.body
                )}
        </div>
    );
};

export default Tooltip;
